import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../images/img.png";
import Icon from "./Icon";
import { NAV, SECTION_IDS, SITE } from "../data/site";
import { PRODUCTS } from "../data/products";
import { useL, useLang } from "../lib/useLang";
import {
  isJoinPath,
  scrollToSection,
  useScrolled,
  useScrollSpy,
} from "../lib/navigation";

function LanguageToggle() {
  const { i18n, t } = useTranslation();
  const lang = useLang();

  const change = (next) => {
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  };

  return (
    <div className="fsg-lang" role="group" aria-label={t("language")}>
      {["es", "en"].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => change(code)}
          aria-pressed={lang === code}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const L = useL();
  const scrolled = useScrolled();
  const active = useScrollSpy(SECTION_IDS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef(null);
  const onJoinPage = isJoinPath();
  const activeId = onJoinPage ? "unete" : active;

  // El drawer bloquea el scroll del fondo mientras está abierto.
  useEffect(() => {
    document.body.classList.toggle("fsg-no-scroll", drawerOpen);
    return () => document.body.classList.remove("fsg-no-scroll");
  }, [drawerOpen]);

  // Cierra el mega menú al hacer clic fuera o al presionar Escape.
  useEffect(() => {
    if (!megaOpen) return undefined;

    const onClick = (event) => {
      if (megaRef.current && !megaRef.current.contains(event.target)) {
        setMegaOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setMegaOpen(false);
    };

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [megaOpen]);

  // Solo interceptamos el clic si la sección existe en esta página;
  // si no, dejamos que el enlace navegue a la portada como cualquier <a>.
  const go = (id) => (event) => {
    setDrawerOpen(false);
    setMegaOpen(false);

    if (onJoinPage || !document.getElementById(id)) return;

    event.preventDefault();
    scrollToSection(id);
  };

  const navHref = (id) => (id === "unete" ? "/unete" : `/#${id}`);

  const goToNavItem = (id) => (event) => {
    setDrawerOpen(false);
    setMegaOpen(false);

    if (id === "unete" || onJoinPage || !document.getElementById(id)) return;

    event.preventDefault();
    scrollToSection(id);
  };

  return (
    <>
      <a className="fsg-skip-link" href="#contenido">
        {t("skipToContent")}
      </a>

      <div className="fsg-topbar">
        <div className="fsg-container">
          <ul>
            <li>
              <Icon name="shield" /> {t("topbar.licensed")}
            </li>
            <li>
              <Icon name="clock" /> {t("topbar.hours")}
            </li>
          </ul>
          <ul>
            <li>
              <a href={`tel:${SITE.phone}`}>
                <Icon name="phone" /> {SITE.phoneDisplay}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${SITE.whatsapp}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="whatsapp" /> WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <header className={`fsg-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="fsg-container">
          <a className="fsg-logo" href="/#inicio" onClick={go("inicio")}>
            <img src={Logo} alt={SITE.name} />
            <span className="fsg-sr-only">{SITE.name}</span>
          </a>

          <nav className="fsg-nav" aria-label="Principal">
            {NAV.map((item) =>
              item.mega ? (
                <div className="fsg-nav__item" key={item.id} ref={megaRef}>
                  <button
                    type="button"
                    className={`fsg-nav__link ${
                      activeId === item.id ? "active" : ""
                    }`}
                    aria-expanded={megaOpen}
                    aria-haspopup="true"
                    onClick={() => setMegaOpen((prev) => !prev)}
                  >
                    {L(item.label)}
                    <Icon name="chevron" className="fsg-nav__caret" />
                  </button>

                  <div className={`fsg-mega ${megaOpen ? "is-open" : ""}`}>
                    <div className="fsg-mega__grid">
                      {PRODUCTS.map((product) => (
                        <a
                          key={product.id}
                          className="fsg-mega__link"
                          href="/#productos"
                          onClick={go("productos")}
                        >
                          <span className="fsg-mega__icon">
                            <Icon name={product.icon} />
                          </span>
                          <span>
                            <strong>{L(product.name)}</strong>
                            <span>{L(product.promise)}</span>
                          </span>
                        </a>
                      ))}
                    </div>
                    <div className="fsg-mega__foot">
                      <p>{t("products.unsureText")}</p>
                      <a
                        className="fsg-btn fsg-btn--primary fsg-btn--sm"
                        href="/#herramientas"
                        onClick={go("herramientas")}
                      >
                        <Icon name="sparkles" />
                        {t("recommender.title")}
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="fsg-nav__item" key={item.id}>
                  <a
                    className={`fsg-nav__link ${
                      activeId === item.id ? "active" : ""
                    }`}
                    href={navHref(item.id)}
                    onClick={goToNavItem(item.id)}
                  >
                    {L(item.label)}
                  </a>
                </div>
              )
            )}
          </nav>

          <div className="fsg-header__actions">
            <LanguageToggle />
            <a
              className="fsg-btn fsg-btn--primary"
              href="/#contacto"
              onClick={go("contacto")}
            >
              {t("cta.primary")}
            </a>
            <button
              type="button"
              className="fsg-burger"
              aria-label={t("menu")}
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="menu" />
            </button>
          </div>
        </div>
      </header>

      {/* Menú móvil */}
      <div
        className={`fsg-drawer ${drawerOpen ? "is-open" : ""}`}
        aria-hidden={!drawerOpen}
      >
        <button
          type="button"
          className="fsg-drawer__backdrop"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className="fsg-drawer__panel"
          role="dialog"
          aria-modal="true"
          aria-label={t("menu")}
        >
          <div className="fsg-drawer__head">
            <img src={Logo} alt={SITE.name} height={38} />
            <button
              type="button"
              className="fsg-drawer__close"
              onClick={() => setDrawerOpen(false)}
              aria-label={t("close")}
            >
              <Icon name="close" />
            </button>
          </div>

          <nav className="fsg-drawer__nav" aria-label="Móvil">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={navHref(item.id)}
                className={activeId === item.id ? "active" : ""}
                onClick={goToNavItem(item.id)}
              >
                {L(item.label)}
              </a>
            ))}

            <span className="fsg-drawer__group-label">
              {t("footer.products")}
            </span>
            {PRODUCTS.map((product) => (
              <a key={product.id} href="/#productos" onClick={go("productos")}>
                {L(product.name)}
              </a>
            ))}

            <span className="fsg-drawer__group-label">
              {t("footer.explore")}
            </span>
            <a href="/#centro-educativo" onClick={go("centro-educativo")}>
              {t("learning.eyebrow")}
            </a>
            <a href="/#faq" onClick={go("faq")}>
              {t("faq.eyebrow")}
            </a>
          </nav>

          <div className="fsg-drawer__foot">
            <LanguageToggle />
            <a
              className="fsg-btn fsg-btn--primary fsg-btn--block"
              href="/#contacto"
              onClick={go("contacto")}
            >
              {t("cta.primaryLong")}
            </a>
            <a
              className="fsg-btn fsg-btn--ghost fsg-btn--block"
              href={`tel:${SITE.phone}`}
            >
              <Icon name="phone" /> {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
